import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type { PlatformAccountSummary } from '@aeris/contracts'
import { formatMoney, money, ZERO_MONEY } from '@aeris/money'
import { TENANT_FEATURES } from '@aeris/permissions'
import {
  Button,
  CheckboxField,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  Textarea,
} from '@aeris/ui'

import {
  fetchAccountAssistant,
  setAccountAssistantCap,
} from '../lib/api/assistant.ts'
import {
  fetchPlatformAccountFeatures,
  fetchPlatformAccountNote,
  fetchPlatformAiCost,
  savePlatformAccountNote,
  setPlatformAccountFeatures,
  type PlatformAccountNoteType,
} from '../lib/api/platform.ts'
import { formatDate } from '../lib/date-format.ts'

import { AccountPages } from './platform-account-pages.tsx'
import { StatusBadge } from './platform-account-status.tsx'
import { AccountWhatsapp } from './platform-account-whatsapp.tsx'

/**
 * A ficha da conta — F154.
 *
 * A tabela mostra nove colunas e ainda deixa de fora cinco coisas que já vêm
 * na mesma resposta: **razão social**, **nome fantasia**, **desde quando a
 * conta existe**, **quantas pessoas ao todo** (contra as ativas, que é o que a
 * coluna "Pessoas" mostra sem dizer) e **o custo de comunicação**. Nenhuma
 * consulta nova: tudo isto já estava carregado e invisível.
 *
 * ## Por que não há "Editar"
 *
 * **Não existe edição de conta.** O `PlatformAccountForm` só cria. Um botão
 * "Editar" aqui abriria coisa nenhuma — e as ações que existem (Medir,
 * Suspender, Reativar, Redefinir senha do dono) continuam na linha, onde a
 * confirmação e o motivo obrigatório já moram.
 */

/** `R$ 1.234,56`, e `R$ 0,00` quando o valor não é um número reconhecível. */
const asMoney = (wire: string): string => {
  try {
    return formatMoney(money(wire))
  } catch {
    return formatMoney(ZERO_MONEY)
  }
}

/**
 * O custo de comunicação, que chega em MICROS de dólar.
 *
 * A conversão é feita em inteiros e o resultado é montado como texto: dividir
 * por 1.000.000 em ponto flutuante para depois arredondar é exatamente o que
 * o ADR-0016 proíbe para dinheiro, e não há razão para abrir exceção num
 * número que só vai ser lido.
 */
const asUsd = (micros: number): string => {
  const cents = Math.round(micros / 10_000)
  const whole = Math.trunc(cents / 100)
  const rest = String(Math.abs(cents % 100)).padStart(2, '0')
  return `US$ ${String(whole)},${rest}`
}

const Line = ({
  label,
  children,
}: {
  label: string
  children: ReactNode
}): ReactNode => (
  <>
    <dt className="text-(--text-muted)">{label}</dt>
    <dd className="text-(--text-primary)">{children}</dd>
  </>
)

/**
 * As chaves desta conta (F169).
 *
 * Um interruptor por chave de `TENANT_FEATURES` — a mesma lista que o produto
 * lê, para que o console não possa oferecer uma chave que o servidor recusa.
 *
 * Sem ação em massa, pelo mesmo motivo da F119: ligar uma vertical em quatro
 * lojas com um clique errado é caro, e o ganho seria conveniência para quem faz
 * isso uma vez por mês.
 */
const AccountFeatures = ({ tenantId }: { tenantId: string }): ReactNode => {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const query = useQuery({
    queryKey: ['platform-features', tenantId],
    queryFn: ({ signal }) => fetchPlatformAccountFeatures(tenantId, signal),
  })

  const save = useMutation({
    mutationFn: async (features: readonly string[]) =>
      await setPlatformAccountFeatures(tenantId, features),
    onSuccess: (features) => {
      queryClient.setQueryData(['platform-features', tenantId], features)
      setError(null)
    },
    onError: () => {
      setError('Não foi possível salvar. Tente de novo.')
    },
  })

  const current = new Set(query.data ?? [])

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
        O que esta conta tem
      </h3>

      {error !== null && (
        <p className="text-(length:--text-xs) text-(--danger)">{error}</p>
      )}

      {TENANT_FEATURES.map((feature) => (
        <div key={feature.key} className="flex flex-col gap-0.5">
          <CheckboxField
            label={feature.label}
            checked={current.has(feature.key)}
            disabled={query.isPending || save.isPending}
            onCheckedChange={(checked) => {
              const next = new Set(current)
              if (checked === true) next.add(feature.key)
              else next.delete(feature.key)
              save.mutate([...next])
            }}
          />
          <p className="pl-6 text-(length:--text-xs) text-(--text-muted)">
            {feature.description}
          </p>
        </div>
      ))}

      {/*
        Desligar não apaga nada do cliente: a tela some do menu e a rota
        recusa, e os dados ficam. Religar traz tudo de volta — a mesma promessa
        que o "esta loja controla estoque" já faz.
      */}
      <p className="text-(length:--text-xs) text-(--text-muted)">
        Desligar esconde as telas e recusa as rotas. Nada é apagado, e religar
        traz tudo de volta.
      </p>
    </div>
  )
}

/**
 * Contato e anotações (F169).
 *
 * Privado da plataforma: a tabela só é alcançável fora de qualquer escopo de
 * loja, e o lojista nunca lê o que se escreve aqui. A tela diz isso, porque a
 * primeira dúvida de quem anota "cliente difícil" é se o cliente vai ver.
 */
const AccountNote = ({ tenantId }: { tenantId: string }): ReactNode => {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<PlatformAccountNoteType | null>(null)
  const [error, setError] = useState<string | null>(null)

  const query = useQuery({
    queryKey: ['platform-note', tenantId],
    queryFn: ({ signal }) => fetchPlatformAccountNote(tenantId, signal),
  })

  const save = useMutation({
    mutationFn: async (note: PlatformAccountNoteType) =>
      await savePlatformAccountNote(tenantId, note),
    onSuccess: (note) => {
      queryClient.setQueryData(['platform-note', tenantId], note)
      setDraft(null)
      setError(null)
    },
    onError: () => {
      setError('Não foi possível salvar. Recarregue e tente de novo.')
    },
  })

  const note = draft ?? query.data
  if (note === undefined) return null

  const edit = (patch: Partial<PlatformAccountNoteType>): void => {
    setDraft({ ...note, ...patch })
  }

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
        Contato e anotações
      </h3>
      <p className="text-(length:--text-xs) text-(--text-muted)">
        Só a Aeris vê. O lojista não tem acesso a nada escrito aqui.
      </p>

      <div className="grid grid-cols-2 gap-2">
        <Field label="Telefone ou WhatsApp">
          {(field) => (
            <Input
              {...field}
              value={note.contactPhone ?? ''}
              onChange={(event) => {
                edit({ contactPhone: event.target.value })
              }}
            />
          )}
        </Field>
        <Field label="E-mail de contato">
          {(field) => (
            <Input
              {...field}
              type="email"
              value={note.contactEmail ?? ''}
              onChange={(event) => {
                edit({ contactEmail: event.target.value })
              }}
            />
          )}
        </Field>
      </div>

      <Field label="Anotações">
        {(field) => (
          <Textarea
            {...field}
            rows={3}
            maxLength={4000}
            value={note.notes}
            onChange={(event) => {
              edit({ notes: event.target.value })
            }}
          />
        )}
      </Field>

      {error !== null && (
        <p className="text-(length:--text-xs) text-(--danger)">{error}</p>
      )}

      {draft !== null && (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="primary"
            loading={save.isPending}
            onClick={() => {
              save.mutate(draft)
            }}
          >
            Salvar anotações
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setDraft(null)
            }}
          >
            Descartar
          </Button>
        </div>
      )}
    </div>
  )
}

/**
 * O custo de IA desta conta (F169).
 *
 * Separado por quem paga: a chave da Aeris é custo nosso, a do lojista é dele.
 * Somar os dois faria o número que diz se o cliente se paga contar o que ele
 * mesmo pagou.
 */
const AccountAiCost = ({ tenantId }: { tenantId: string }): ReactNode => {
  const query = useQuery({
    queryKey: ['platform-ai-cost', tenantId],
    queryFn: ({ signal }) => fetchPlatformAiCost(tenantId, signal),
  })

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
        Custo de IA
      </h3>
      {query.data === undefined ? null : query.data.length === 0 ? (
        <p className="text-(length:--text-sm) text-(--text-muted)">
          Esta conta ainda não usou IA.
        </p>
      ) : (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-(length:--text-sm)">
          {query.data.map((month) => (
            <Line
              key={month.periodStart}
              label={formatDate(month.periodStart).slice(3)}
            >
              {asUsd(month.platformMicrosUsd)} nosso
              {month.tenantMicrosUsd > 0
                ? ` · ${asUsd(month.tenantMicrosUsd)} da chave dele`
                : ''}
            </Line>
          ))}
        </dl>
      )}
    </div>
  )
}

/**
 * O assistente desta conta (F256): o gasto do mês com o GPT do Aeris e o teto.
 * O teto vazio volta ao padrão da Plataforma.
 */
const AccountAssistant = ({ tenantId }: { tenantId: string }): ReactNode => {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: ['platform-account-assistant', tenantId],
    queryFn: async () => await fetchAccountAssistant(tenantId),
  })
  const [cap, setCap] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: async (value: string | null) => {
      await setAccountAssistantCap(tenantId, value)
    },
    onSuccess: async () => {
      setCap(null)
      await queryClient.invalidateQueries({
        queryKey: ['platform-account-assistant', tenantId],
      })
    },
  })
  const data = query.data
  if (data === undefined) return null
  const shown = cap ?? data.monthlyCapUsd ?? ''

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
        Assistente no WhatsApp
      </h3>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-(length:--text-sm)">
        <Line label="Ligado">{data.enabled ? 'sim' : 'não'}</Line>
        <Line label="Pessoas ligadas">{data.linkedPeople}</Line>
        <Line label="Neste mês">
          US$ {data.spentMonthUsd} em {data.questionsMonth}{' '}
          {data.questionsMonth === 1 ? 'pergunta' : 'perguntas'}
        </Line>
      </dl>
      <form
        className="flex items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          const value = shown.trim().replace(',', '.')
          save.mutate(value === '' ? null : value)
        }}
      >
        <Field
          label="Teto por mês (US$)"
          hint={`Vazio: o padrão, US$ ${data.defaultMonthlyCapUsd}.`}
        >
          {(field) => (
            <Input
              {...field}
              inputMode="decimal"
              value={shown}
              placeholder={data.defaultMonthlyCapUsd}
              onChange={(event) => {
                setCap(event.target.value)
              }}
            />
          )}
        </Field>
        <Button
          type="submit"
          size="sm"
          variant="secondary"
          loading={save.isPending}
          disabled={cap === null}
        >
          Salvar teto
        </Button>
      </form>
    </div>
  )
}

export const PlatformAccountDialog = ({
  account,
  onClose,
}: {
  account: PlatformAccountSummary
  onClose: () => void
}): ReactNode => (
  <Dialog open onOpenChange={onClose}>
    <DialogContent
      title={account.name}
      description="O cadastro, o uso, o que esta conta tem e o que a Aeris anotou sobre ela."
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={account.status} />
          <span className="font-(--font-mono) text-(length:--text-xs) text-(--text-muted)">
            {account.slug}
          </span>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-(length:--text-sm)">
          <Line label="Razão social">{account.legalName ?? '—'}</Line>
          <Line label="Nome fantasia">{account.tradeName ?? '—'}</Line>
          <Line label="CNPJ">
            <span className="font-(--font-mono)">{account.cnpj ?? '—'}</span>
          </Line>
          <Line label="Dono">{account.ownerEmail ?? '—'}</Line>
          <Line label="Cliente desde">{formatDate(account.createdAt)}</Line>
          {/* Duas contagens diferentes, e a tabela só mostrava a segunda sob o
              rótulo ambíguo "Pessoas". Quem tem 12 cadastros e 3 entrando é um
              caso; quem tem 3 e 3 é outro. */}
          <Line label="Pessoas cadastradas">{account.memberCount}</Line>
          <Line label="Pessoas ativas">
            {account.usage?.activeUserCount ?? 'ainda não medido'}
          </Line>
        </dl>

        <div className="flex flex-col gap-2">
          <h3 className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
            Uso
          </h3>
          {account.usage === null ? (
            /*
             * "Ainda não medido", e nunca zero.
             * Zero é uma afirmação — a de que a loja não tem nada —, e ela é
             * falsa sobre uma conta que nasceu hoje e que ninguém contou.
             */
            <p className="text-(length:--text-sm) text-(--text-muted)">
              Esta conta ainda não foi medida.
            </p>
          ) : (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-(length:--text-sm)">
              {/* A que mês as vendas se referem — a tabela deixava o leitor
                  supor que era o corrente. */}
              <Line label="Período">
                a partir de {formatDate(account.usage.periodStart)}
              </Line>
              <Line label="Medido em">
                {formatDate(account.usage.capturedAt)}
              </Line>
              <Line label="Produtos">{account.usage.productCount}</Line>
              <Line label="Vendas">{account.usage.salesCount}</Line>
              <Line label="Valor vendido">
                {asMoney(account.usage.salesAmount)}
              </Line>
              {/* O que esta conta custa em WhatsApp e afins. Invisível na
                  tabela, e é o número que decide se um plano se paga. */}
              <Line label="Custo de comunicação">
                {asUsd(account.usage.communicationCostMicrosUsd)}
              </Line>
            </dl>
          )}
        </div>

        <AccountAiCost tenantId={account.tenantId} />

        <AccountAssistant tenantId={account.tenantId} />

        <AccountFeatures tenantId={account.tenantId} />

        <AccountPages tenantId={account.tenantId} />

        <AccountWhatsapp tenantId={account.tenantId} />

        <AccountNote tenantId={account.tenantId} />
      </div>

      <DialogFooter>
        <Button variant="secondary" onClick={onClose}>
          Fechar
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
)
